import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    CheckCircle,
    XCircle,
    Tray,
    Trash,
    ArrowCounterClockwise,
    ClockCounterClockwise,
    NotePencil,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_LABELS = {
    vacaciones: "Vacaciones",
    ausencia: "Ausencia personal",
    baja: "Baja médica",
    formacion: "Formación",
    personal: "Personal",
};
const STATUS_STYLES = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-800",
};
const STATUS_LABEL = {
    pending: "Pendiente",
    approved: "Aprobada",
    rejected: "Rechazada",
};

function StatusBadge({ status }) {
    return (
        <span
            className={`px-2 py-1 rounded-sm text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLES[status]}`}
        >
            {STATUS_LABEL[status]}
        </span>
    );
}

function StatusTabs({ value, onChange }) {
    return (
        <div className="flex gap-2 mb-5">
            {[
                { v: "pending", l: "Pendientes" },
                { v: "approved", l: "Aprobadas" },
                { v: "rejected", l: "Rechazadas" },
                { v: "all", l: "Todas" },
            ].map((t) => (
                <button
                    key={t.v}
                    onClick={() => onChange(t.v)}
                    className={`px-4 py-2 text-sm border rounded-sm transition-colors ${
                        value === t.v
                            ? "bg-slate-950 text-white border-slate-950"
                            : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`status-tab-${t.v}`}
                >
                    {t.l}
                </button>
            ))}
        </div>
    );
}

function AbsenceList() {
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("pending");
    const [historyUser, setHistoryUser] = useState(null);

    const load = async () => {
        const params = { all_users: true };
        if (status !== "all") params.status_filter = status;
        const r = await http.get("/absences", { params });
        setItems(r.data);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const decide = async (id, newStatus) => {
        try {
            await http.put(`/absences/${id}`, { status: newStatus });
            if (newStatus === "pending") {
                toast.success("Decisión deshecha · vuelve a Pendiente");
            } else {
                toast.success(
                    newStatus === "approved"
                        ? "Solicitud aprobada"
                        : "Solicitud rechazada",
                    {
                        duration: 8000,
                        action: {
                            label: "Deshacer",
                            onClick: () => decide(id, "pending"),
                        },
                    },
                );
            }
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const remove = async (a) => {
        if (
            !window.confirm(
                `¿Eliminar definitivamente la solicitud de ${a.user_name} (${a.start_date} → ${a.end_date})?\n\nSi estaba aprobada, también se borrarán las horas de vacaciones/ausencia asociadas.`,
            )
        )
            return;
        try {
            await http.delete(`/absences/${a.request_id}`);
            toast.success("Solicitud eliminada");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    return (
        <div>
            <StatusTabs value={status} onChange={setStatus} />
            <div className="border border-slate-200 bg-white">
                {items.length === 0 ? (
                    <div className="text-center py-16 text-slate-500">
                        <Tray size={32} className="mx-auto text-slate-300 mb-3" />
                        <div className="font-display text-lg font-semibold text-slate-700">
                            No hay solicitudes
                        </div>
                    </div>
                ) : (
                    <ul data-testid="absence-list">
                        {items.map((a) => (
                            <li
                                key={a.request_id}
                                className="border-b border-slate-100 px-6 py-5 flex items-center gap-6 flex-wrap"
                                data-testid={`absence-row-${a.request_id}`}
                            >
                                <div className="flex-1 min-w-[260px]">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                        {TYPE_LABELS[a.type] || a.type}
                                    </div>
                                    <div className="font-display text-lg font-semibold mt-1">
                                        {a.user_name}
                                    </div>
                                    <div className="text-sm text-slate-600 mt-1 font-mono">
                                        {a.start_date} → {a.end_date} · {a.days} días ·{" "}
                                        {a.hours_per_day} h/día
                                    </div>
                                    {a.reason && (
                                        <div className="text-sm text-slate-600 mt-2 italic">
                                            “{a.reason}”
                                        </div>
                                    )}
                                    {a.admin_notes && (
                                        <div className="text-sm text-[#002BAF] mt-2 flex items-start gap-1.5">
                                            <NotePencil size={14} className="mt-0.5 shrink-0" />
                                            <span>{a.admin_notes}</span>
                                        </div>
                                    )}
                                    {a.decided_by && a.status !== "pending" && (
                                        <div className="text-[11px] text-slate-400 mt-1">
                                            {STATUS_LABEL[a.status]} por {a.decided_by}
                                        </div>
                                    )}
                                </div>
                                <StatusBadge status={a.status} />
                                <div className="flex gap-2 items-center flex-wrap justify-end">
                                    <button
                                        onClick={() =>
                                            setHistoryUser({
                                                user_id: a.user_id,
                                                user_name: a.user_name,
                                            })
                                        }
                                        title="Ver histórico de la persona"
                                        className="text-slate-500 hover:text-[#002BAF] p-2 rounded-sm border border-slate-200"
                                        data-testid={`absence-history-${a.request_id}`}
                                    >
                                        <ClockCounterClockwise size={16} />
                                    </button>
                                    {a.status === "pending" ? (
                                        <>
                                            <Button
                                                variant="outline"
                                                onClick={() =>
                                                    decide(a.request_id, "rejected")
                                                }
                                                className="rounded-sm border-red-200 text-red-700 hover:bg-red-50"
                                                data-testid={`absence-reject-${a.request_id}`}
                                            >
                                                <XCircle size={16} className="mr-2" /> Rechazar
                                            </Button>
                                            <Button
                                                onClick={() =>
                                                    decide(a.request_id, "approved")
                                                }
                                                className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                                data-testid={`absence-approve-${a.request_id}`}
                                            >
                                                <CheckCircle size={16} className="mr-2" />{" "}
                                                Aprobar
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            variant="outline"
                                            onClick={() => decide(a.request_id, "pending")}
                                            className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50"
                                            title="Deshacer la decisión y volver a Pendiente"
                                            data-testid={`absence-undo-${a.request_id}`}
                                        >
                                            <ArrowCounterClockwise size={16} className="mr-2" />{" "}
                                            Deshacer
                                        </Button>
                                    )}
                                    <button
                                        onClick={() => remove(a)}
                                        title="Eliminar solicitud"
                                        className="text-slate-400 hover:text-red-600 p-2 rounded-sm"
                                        data-testid={`absence-delete-${a.request_id}`}
                                    >
                                        <Trash size={16} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <PersonHistorySheet
                person={historyUser}
                onClose={() => setHistoryUser(null)}
                onChanged={load}
            />
        </div>
    );
}

function PersonHistorySheet({ person, onClose, onChanged }) {
    const [rows, setRows] = useState([]);
    const [year, setYear] = useState(new Date().getFullYear());
    const [busy, setBusy] = useState(false);

    const load = async () => {
        if (!person) return;
        setBusy(true);
        try {
            const r = await http.get("/absences", {
                params: { all_users: true, user_id: person.user_id },
            });
            setRows(r.data);
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [person]);

    const yearRows = useMemo(
        () => rows.filter((r) => (r.start_date || "").slice(0, 4) === String(year)),
        [rows, year],
    );
    const approvedVacDays = useMemo(
        () =>
            yearRows
                .filter((r) => r.type === "vacaciones" && r.status === "approved")
                .reduce((s, r) => s + (Number(r.days) || 0), 0),
        [yearRows],
    );
    const years = useMemo(() => {
        const ys = new Set(rows.map((r) => (r.start_date || "").slice(0, 4)).filter(Boolean));
        ys.add(String(new Date().getFullYear()));
        return [...ys].sort().reverse();
    }, [rows]);

    const saveNotes = async (id, text) => {
        try {
            await http.patch(`/absences/${id}/admin-notes`, { admin_notes: text });
            toast.success("Nota guardada");
            load();
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    return (
        <Sheet open={!!person} onOpenChange={(v) => !v && onClose()}>
            <SheetContent
                className="rounded-none sm:max-w-xl overflow-y-auto"
                data-testid="person-history-sheet"
            >
                <SheetHeader>
                    <SheetTitle className="font-display">
                        Histórico · {person?.user_name}
                    </SheetTitle>
                    <SheetDescription className="text-xs text-slate-500">
                        Trazabilidad de todas las solicitudes y cambios.
                    </SheetDescription>
                </SheetHeader>
                <div className="mt-5 flex items-center gap-3">
                    <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                        <SelectTrigger
                            className="w-32 rounded-sm border-slate-300 h-9"
                            data-testid="history-year"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {years.map((y) => (
                                <SelectItem key={y} value={y}>
                                    {y}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className="flex-1 bg-[#002BAF] text-white px-4 py-2 rounded-sm">
                        <span className="text-[10px] uppercase tracking-widest opacity-80">
                            Vacaciones aprobadas {year}
                        </span>
                        <div
                            className="font-mono text-xl font-bold"
                            data-testid="history-vac-count"
                        >
                            {approvedVacDays} días
                        </div>
                    </div>
                </div>

                <div className="mt-5 space-y-3">
                    {busy && <div className="text-sm text-slate-500">Cargando…</div>}
                    {!busy && yearRows.length === 0 && (
                        <div className="text-sm text-slate-400">
                            Sin solicitudes en {year}.
                        </div>
                    )}
                    {yearRows.map((r) => (
                        <HistoryCard key={r.request_id} r={r} onSaveNotes={saveNotes} />
                    ))}
                </div>
            </SheetContent>
        </Sheet>
    );
}

function HistoryCard({ r, onSaveNotes }) {
    const [notes, setNotes] = useState(r.admin_notes || "");
    const [editing, setEditing] = useState(false);
    return (
        <div className="border border-slate-200 rounded-sm p-4" data-testid={`history-card-${r.request_id}`}>
            <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-mono text-slate-700">
                    {r.start_date} → {r.end_date} · {r.days} d
                </div>
                <StatusBadge status={r.status} />
            </div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-1">
                {TYPE_LABELS[r.type] || r.type}
            </div>
            {r.reason && (
                <div className="text-sm text-slate-600 mt-2 italic">“{r.reason}”</div>
            )}
            {/* Timeline de auditoría */}
            {(r.history || []).length > 0 && (
                <ul className="mt-3 border-l-2 border-slate-100 pl-3 space-y-1">
                    {(r.history || []).map((h, i) => (
                        <li key={i} className="text-[11px] text-slate-500">
                            <span className="font-semibold text-slate-700">
                                {STATUS_LABEL[h.to] || h.action}
                            </span>{" "}
                            · {h.by} · {(h.at || "").slice(0, 16).replace("T", " ")}
                            {h.note ? ` — “${h.note}”` : ""}
                        </li>
                    ))}
                </ul>
            )}
            {/* Notas del responsable */}
            <div className="mt-3">
                {editing ? (
                    <div className="space-y-2">
                        <Textarea
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Nota / comentario del responsable…"
                            className="rounded-sm border-slate-300 text-sm"
                            data-testid={`history-notes-input-${r.request_id}`}
                        />
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                onClick={() => {
                                    onSaveNotes(r.request_id, notes);
                                    setEditing(false);
                                }}
                                className="rounded-sm bg-[#002BAF] hover:bg-[#001F82] h-8"
                                data-testid={`history-notes-save-${r.request_id}`}
                            >
                                Guardar nota
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    setNotes(r.admin_notes || "");
                                    setEditing(false);
                                }}
                                className="rounded-sm h-8"
                            >
                                Cancelar
                            </Button>
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={() => setEditing(true)}
                        className="text-xs text-[#002BAF] hover:underline flex items-center gap-1"
                        data-testid={`history-notes-edit-${r.request_id}`}
                    >
                        <NotePencil size={13} />
                        {r.admin_notes ? "Editar nota del responsable" : "Añadir nota del responsable"}
                    </button>
                )}
                {!editing && r.admin_notes && (
                    <div className="text-sm text-slate-700 mt-1 bg-slate-50 border border-slate-100 p-2 rounded-sm">
                        {r.admin_notes}
                    </div>
                )}
            </div>
        </div>
    );
}

function AssignmentRequestList() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("pending");

    const load = async () => {
        const params = { all_users: true };
        if (status !== "all") params.status_filter = status;
        const r = await http.get("/assignment-requests", { params });
        setItems(r.data);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const resetAll = async () => {
        if (
            !window.confirm(
                "Vas a ELIMINAR permanentemente TODAS las solicitudes de asignación (pendientes, aprobadas y rechazadas). Esta acción no se puede deshacer. ¿Continuar?",
            )
        )
            return;
        try {
            const r = await http.delete("/assignment-requests/reset");
            toast.success(`${r.data.deleted} solicitud(es) eliminadas`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const decide = async (id, newStatus) => {
        try {
            await http.put(`/assignment-requests/${id}`, { status: newStatus });
            toast.success(
                newStatus === "approved"
                    ? "Asignación creada"
                    : "Solicitud rechazada",
            );
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between">
                <StatusTabs value={status} onChange={setStatus} />
                {isAdmin && (
                    <Button
                        variant="outline"
                        onClick={resetAll}
                        className="rounded-sm border-red-200 text-red-700 hover:bg-red-50 mb-5"
                        data-testid="asgreq-reset-all"
                    >
                        <XCircle size={14} className="mr-2" /> Vaciar todo el historial
                    </Button>
                )}
            </div>
            <div className="border border-slate-200 bg-white">
                {items.length === 0 ? (
                    <div className="text-center py-16 text-slate-500">
                        <Tray size={32} className="mx-auto text-slate-300 mb-3" />
                        <div className="font-display text-lg font-semibold text-slate-700">
                            No hay solicitudes
                        </div>
                    </div>
                ) : (
                    <ul data-testid="assignment-request-list">
                        {items.map((r) => (
                            <li
                                key={r.request_id}
                                className="border-b border-slate-100 px-6 py-5 flex items-center gap-6 flex-wrap"
                                data-testid={`asgreq-row-${r.request_id}`}
                            >
                                <div className="flex-1 min-w-[260px]">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                        Asignación a proyecto
                                    </div>
                                    <div className="font-display text-lg font-semibold mt-1">
                                        {r.user_name}{" "}
                                        <span className="text-slate-400 font-normal text-base">
                                            →
                                        </span>{" "}
                                        {r.project_name}
                                    </div>
                                    <div className="text-sm text-slate-600 mt-1 font-mono">
                                        {r.customer_name} · {r.project_code || "—"}
                                    </div>
                                    {r.reason && (
                                        <div className="text-sm text-slate-600 mt-2 italic">
                                            “{r.reason}”
                                        </div>
                                    )}
                                </div>
                                <StatusBadge status={r.status} />
                                {r.status === "pending" && (
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            onClick={() =>
                                                decide(r.request_id, "rejected")
                                            }
                                            className="rounded-sm border-red-200 text-red-700 hover:bg-red-50"
                                            data-testid={`asgreq-reject-${r.request_id}`}
                                        >
                                            <XCircle size={16} className="mr-2" /> Rechazar
                                        </Button>
                                        <Button
                                            onClick={() =>
                                                decide(r.request_id, "approved")
                                            }
                                            className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                            data-testid={`asgreq-approve-${r.request_id}`}
                                        >
                                            <CheckCircle size={16} className="mr-2" />{" "}
                                            Aprobar
                                        </Button>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default function AdminApprovals() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [section, setSection] = useState(isAdmin ? "absences" : "assignments");

    return (
        <div data-testid="page-approvals">
            <PageHeader
                eyebrow={isAdmin ? "Administración" : "Gestión"}
                title="Aprobaciones"
                description={
                    isAdmin
                        ? "Revisa solicitudes de ausencias y asignación a proyectos."
                        : "Solicitudes de asignación para los proyectos que gestionas."
                }
            />
            <div className="p-6 lg:p-10">
                {isAdmin && (
                    <div
                        className="flex gap-2 mb-5 border-b border-slate-200 pb-3"
                        data-testid="primary-tabs"
                    >
                        {[
                            { v: "absences", l: "Ausencias" },
                            { v: "assignments", l: "Asignaciones" },
                        ].map((t) => (
                            <button
                                key={t.v}
                                onClick={() => setSection(t.v)}
                                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-3 ${
                                    section === t.v
                                        ? "border-[#002BAF] text-slate-950"
                                        : "border-transparent text-slate-500 hover:text-slate-800"
                                }`}
                                data-testid={`primary-tab-${t.v}`}
                            >
                                {t.l}
                            </button>
                        ))}
                    </div>
                )}

                {isAdmin && section === "absences" ? (
                    <AbsenceList />
                ) : (
                    <AssignmentRequestList />
                )}
            </div>
        </div>
    );
}
